import { getPlatformFavicon } from '../../utils/platformFavicon';

/**
 * Computes progress and optional cap for a lane step event.
 * If the message contains a "(idx / total)" slot pattern, uses slot math.
 * Otherwise falls back to step-count heuristic.
 */
export function computeStepProgress(
  message: string,
  steps: number,
): { progress: number; cap?: number } {
  const slotMatch = /\((\d+)\s*\/\s*(\d+)\)/.exec(message);
  if (slotMatch) {
    const idx = Number(slotMatch[1]);
    const total = Number(slotMatch[2]);
    if (total > 0 && idx > 0) {
      const slot = 1 / total;
      const isDone = /ready|done|complete/i.test(message);
      return {
        progress: isDone ? idx * slot : (idx - 1) * slot + slot * 0.15,
        cap: idx * slot - slot * 0.08,
      };
    }
  }
  return { progress: Math.min(0.1 + steps * 0.12, 0.9) };
}

/**
 * Derives a display icon for a lane step, if applicable.
 * Knowledge lane → Google favicon for the fetched URL.
 * Trust lane → platform logo via platformFavicon util.
 */
export function deriveStepIcon(
  laneKey: string,
  label: string,
  detail: Record<string, unknown> | undefined,
): { url: string; label: string } | null {
  if (laneKey === 'knowledge' && label === 'fetching-page' && detail?.url) {
    try {
      const domain = new URL(detail.url as string).hostname;
      return { url: `https://www.google.com/s2/favicons?domain=${domain}&sz=64`, label: domain };
    } catch { /* ignore invalid URL */ }
  }
  if (laneKey === 'trust' && label === 'fetching' && detail?.platform) {
    return getPlatformFavicon(detail.platform as string);
  }
  return null;
}

/** Parses a raw SSE data string. Returns null on invalid JSON. */
export function parseStreamMessage(rawData: string): Record<string, unknown> | null {
  try {
    return JSON.parse(rawData) as Record<string, unknown>;
  } catch {
    return null;
  }
}
